import { Disclosure, Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { CaretUpDownIcon, GlobeIcon, ListIcon, UserCircleIcon } from '@phosphor-icons/react';
import { Formik } from 'formik';
import i18n from 'i18next';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Responsive, SelectBox, Skeleton, Text, ZoomButton } from '@components';
import { ROUTES, SCREEN_BREAKPOINTS } from '@constants';
import { Dictionaries, Language, Translation, VariantType } from '@enums';
import { useAppDispatch, useMediaQuery } from '@hooks';
import { logOut, updateWorkspaceId, useUserDetails, useWorkspaceOptions, useWorkspaceOptionsLoading } from '@store';
import { capitalize, cn, getOptionListFromCatalog, validateValueEqual } from '@utils';
import { initData, updateDataInStorage, workSpaceId } from 'storage';

export const Header = ({ sidebarAction }) => {
  const { t: tCommon } = useTranslation(Translation.Common);
  const { t: tAll } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const path = useLocation();
  const userDetails = useUserDetails();
  const workSpaceList = useWorkspaceOptions();
  const workspaceLoading = useWorkspaceOptionsLoading();
  const init = initData.getItem();
  const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

  const currentWorkspaceId = workSpaceId.getItem();

  const [isFullScreen, setIsFullScreen] = useState(false);
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);

    if (!isFullScreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const pageDetails = (path: { pathname: string }) => {
    const { pathname } = path;
    const firstPath = path.pathname.split('/')[1];
    for (const key in ROUTES) {
      if (Object.hasOwn(ROUTES, key)) {
        const route = ROUTES[key];
        if (typeof route === 'string' && route === pathname) {
          return pathname.slice(1);
        }
      }
    }
    return validateValueEqual(firstPath, 'workspace', 'string') ? 'workspace' : 'configuration';
  };

  const languageOption = useMemo(() => getOptionListFromCatalog({ name: Dictionaries.Languages }), [tAll]);
  const workspaceOptions = useMemo(
    () =>
      workSpaceList?.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [tAll, workspaceLoading],
  );

  const layoutTitleRow = () => {
    return (
      <div className="flex flex-col gap-4 sm:gap-4 w-full xl:w-2/5 ">
        <Text $level={3} color="text-theme-text-title" className=" font-semibold  ">
          {tAll(`${pageDetails(path)}:${path.pathname.includes('invite') ? 'inviteTitle' : 'title'}`)}
        </Text>
        <Text $level={5} color="text-gray-400" className="font-medium text-justify leading-4">
          {tAll(`${pageDetails(path)}:${path.pathname.includes('invite') ? 'inviteDescription' : 'description'}`, {
            name: capitalize(`${userDetails?.first_name} ${userDetails?.last_name}`),
          })}
        </Text>
      </div>
    );
  };

  const menuItemClassName =
    'rounded-lg block w-full text-left px-3.5 py-3 font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-900 enabled:shadow-none';

  return (
    <Disclosure as="nav" className={'pb-8'}>
      {() => (
        <div className="flex flex-col gap-8">
          <div className={`flex flex-row w-full gap-3 items-center relative`}>
            <Responsive showBelow="xl">
              <div className="h-full justify-start flex">
                <Button
                  variant={VariantType.Clean}
                  hoverEffect
                  className="text-theme-default rounded-lg p-3 enabled:shadow-none"
                  onClick={() => sidebarAction(true)}
                  adornment={<ListIcon className="size-5 text-black" />}
                />
              </div>
            </Responsive>
            <Responsive showAbove="xl">{layoutTitleRow()}</Responsive>
            <Menu as="div" className="flex flex-1 justify-end">
              <div className="flex items-center gap-3 md:gap-6">
                {workspaceLoading && workspaceOptions.length <= 0 ? (
                  <Skeleton className="w-4 h-4" />
                ) : (
                  <Formik
                    initialValues={{
                      language: init?.language ?? Language.English,
                      workspace_id: currentWorkspaceId,
                    }}
                    enableReinitialize
                    onSubmit={() => {}}
                  >
                    {() => {
                      const handleChangeLanguage = (option) => {
                        if (option.value) {
                          i18n.changeLanguage(option.value);
                          updateDataInStorage(initData, ['language'], {
                            language: option.value,
                          });
                        }
                      };
                      const handleChangeWorkspace = (option) => {
                        if (option.value) {
                          dispatch(updateWorkspaceId(option.value));
                          if (!validateValueEqual(path.pathname, ROUTES.cdn)) {
                            navigate(ROUTES.cdn);
                          }
                        }
                      };
                      return (
                        <>
                          {isAboveSm && (
                            <SelectBox
                              name="language"
                              options={languageOption}
                              customMenu={
                                <div className="flex gap-1 items-center p-0.5">
                                  <GlobeIcon className="size-3 sm:size-4 text-gray-600" />
                                  <CaretUpDownIcon weight="bold" className="size-3 sm:size-4 text-gray-400" />
                                </div>
                              }
                              onOptionSelect={handleChangeLanguage}
                            />
                          )}

                          <SelectBox
                            name="workspace_id"
                            className="min-w-28 sm:min-w-40 max-w-28 sm:max-w-64"
                            options={workspaceOptions}
                            onOptionSelect={handleChangeWorkspace}
                          />
                        </>
                      );
                    }}
                  </Formik>
                )}
                <ZoomButton isFullScreen={isFullScreen} toggleFullScreen={toggleFullScreen} />
                <MenuButton>
                  <UserCircleIcon className="text-gray-600 size-6 sm:size-7" weight="fill" />
                </MenuButton>
              </div>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-1 scale-95"
              >
                <MenuItems className="absolute top-10 right-0 mt-4 w-40 origin-top-right text-xs rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <MenuItem>
                      {({ focus }) => (
                        <Button
                          onClick={() => navigate(ROUTES.profileSettings)}
                          variant={VariantType.Clean}
                          className={cn(focus && 'bg-gray-100 text-gray-900', menuItemClassName)}
                        >
                          {tCommon('button.yourProfile')}
                        </Button>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ focus }) => (
                        <Button
                          onClick={() => dispatch(logOut())}
                          variant={VariantType.Clean}
                          className={cn(focus && 'bg-gray-100 text-gray-900', menuItemClassName, 'text-red-600')}
                        >
                          {tCommon('button.signOut')}
                        </Button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
          <Responsive showBelow="xl">{layoutTitleRow()}</Responsive>
        </div>
      )}
    </Disclosure>
  );
};
